import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Response } from '@angular/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PointsService } from '../../../shared/services/points.service';
import { ResumeUpdaterService } from '../../../cart/services/cart-resume-updater.service';

/**
 * "Use my points" control at the payment step — coupon's sibling. Applying only
 * stores the intention on the cart session (server debits when the order is
 * registered). Renders NOTHING unless the store gate is on AND the redemption
 * availability confirms enabled with something to redeem — a store without the
 * program (or a cart that cannot take points, e.g. subscriptions) stays
 * identical to today.
 */
@Component({
    selector: 'payment-payment-points',
    templateUrl: './payment.payment.points.component.html',
    styleUrls: []
})
export class PaymentPaymentPointsComponent implements OnInit {
    public show = false;
    public inputting = false;
    public loading = false;
    public applied = 0;
    public balance = 0;
    public minimum = 0;
    public maxRedeemable = 0;
    public maxRedeemableCurrency = 0;
    public pointsToApply: number;

    private gateOn = false;

    constructor(
        protected points: PointsService,
        protected resumeUpdaterService: ResumeUpdaterService,
        protected toastr: ToastrService,
        protected translate: TranslateService,
        protected activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        const configurations = this.activatedRoute.snapshot.data['storeConfigurations'] || {};
        this.gateOn = !!(+configurations['points-program-enabled'] || false);

        this.loadAvailability();
    }

    /** Apply N points and refresh the resume (discount shows on the totals). */
    public apply() {
        const points = +this.pointsToApply;
        if (!points || points < this.minimum || points > this.maxRedeemable) {
            return;
        }

        this.loading = true;
        this.points.apply(points).subscribe(() => {
            this.inputting = false;
            this.refreshAfterChange();
        }, error => {
            this.loading = false;
            this.notifyError(error);
        });
    }

    /** Remove the applied points and refresh the resume. */
    public remove() {
        this.loading = true;
        this.points.reset().subscribe(() => {
            this.pointsToApply = null;
            this.refreshAfterChange();
        }, () => {
            this.refreshAfterChange();
        });
    }

    public cancel() {
        this.inputting = false;
        this.pointsToApply = null;
    }

    /**
     * Availability decides everything: gate off, API off, no balance and not
     * logged yet (401 swallowed) all end at show=false.
     */
    private loadAvailability() {
        if (!this.gateOn) {
            return;
        }

        this.points.availability().subscribe((response: Response) => {
            const data = response.json().data;

            if (!data['enabled']) {
                this.show = false;
                return;
            }

            this.minimum = data['minimum'];
            this.balance = data['balance'] || 0;
            this.maxRedeemable = data['max_redeemable'];
            this.maxRedeemableCurrency = data['max_redeemable_currency'] || 0;
            this.applied = data['applied_points'];
            // Only offer when something is actually applicable: with a balance
            // below the store minimum (max < minimum) there is nothing the
            // client could apply — showing the box would be a dead end.
            this.show = this.applied > 0
                || (this.maxRedeemable > 0 && this.maxRedeemable >= this.minimum);
        }, () => {
            this.show = false;
        });
    }

    private refreshAfterChange() {
        // Fire updated$ so the card component reloads the installment options
        // against the new total — the coupon flow emits the same signal.
        this.resumeUpdaterService.notifyUpdated();
        this.resumeUpdaterService.refresh().subscribe(() => {
            this.loading = false;
            this.loadAvailability();
        }, () => {
            this.loading = false;
            this.loadAvailability();
        });
    }

    /** Backend business message (ValidationException on `points`) or fallback. */
    private notifyError(error) {
        let message = null;
        if (error && error['errors'] && error['errors']['points']) {
            message = error['errors']['points'].join(' ');
        }

        if (message) {
            this.toastr.error(message);
            return;
        }

        this.translate.get('PAYMENT.PAYMENT.POINTS.GENERIC_ERROR').subscribe(translated => {
            this.toastr.error(translated);
        });
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src3894461559/src/src/app/payment/payment/points/payment.payment.points.component.ts